import {useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Redirect, Switch} from 'react-router-dom';

import type {User} from 'nutshell-core/types';

import {ConnectedFormsListPage} from '../../ui/connected-forms/connected-forms-list/connected-forms-list-page';
import {ConnectedFormsPage} from '../../ui/connected-forms/connected-forms-page/connected-forms-page';
import {AnalyticsPage} from '../../ui/email-marketing/analytics/analytics-page';
import {CompetitorsPageWrapper} from '../../ui/email-marketing/competitors/competitors-page-wrapper';
import {Dashboard} from '../../ui/email-marketing/dashboard/dashboard';
import {CrmAdvertisingPage} from '../../ui/email-marketing/lead-generation/crm-advertising/crm-advertising-page';
import {MarketingResourcesPage} from '../../ui/email-marketing/lead-generation/marketing-resources/marketing-resources-page';
import {MarketingServicesPage} from '../../ui/email-marketing/lead-generation/marketing-services/marketing-services-page';
import {ProspectorListPage} from '../../ui/email-marketing/lead-generation/prospector/prospector-page';
import {SEOScanPage} from '../../ui/email-marketing/lead-generation/seo-scan/seo-scan-page';
import {MoneyPage} from '../../ui/email-marketing/money-pages/money-page';
import {MoneyPagesListPage} from '../../ui/email-marketing/money-pages/money-pages-list-page';
import {
    EmailMarketingRoutes,
    getEmailMarketingSidebar,
    getPageContainerHeader,
    shouldHideMarketingSidebar,
} from '../../ui/email-marketing/routes';
import {SmsMarketingRoutes} from '../../ui/email-marketing/routes/shared-routes';
import {VisitorsPageWrapper} from '../../ui/email-marketing/visitors/visitors-page-wrapper';
import {FormPage} from '../../ui/forms/form-page/form-page';
import {FormHomePage} from '../../ui/forms/form-page/multi-page-forms/form-home-page';
import {FormPreview} from '../../ui/forms/form-preview/form-preview';
import {fetchSchemaAction} from '../../ui/forms/forms-actions';
import {FormsListPage} from '../../ui/forms/forms-list/forms-list-page';
import {PageWrapper} from '../../ui/layout/page-wrapper';
import {DASHBOARD} from '../../ui/reports/report-types';

import {RestrictedListPageRoute} from '../shared/restricted-list-page-route';
import {RestrictedPageRoute} from '../shared/restricted-page-route';
import {RestrictedRoute} from '../shared/restricted-route';

const FORM_VIEW_SEARCH = 'FORM_VIEW_SEARCH';
const CONNECTED_FORMS_SEARCH = 'CONNECTED_FORMS_SEARCH';
const PROSPECTOR_VIEW_SEARCH = 'PROSPECTOR_VIEW_SEARCH';

export const DEFAULT_EMAIL_MARKETING_REDIRECT_PATH = '/marketing/email/summary';

type Props = {
    user: User | null | undefined;
};

export function MarketingRoot(props: Props) {
    const dispatch = useDispatch();

    useEffect(() => {
        // @ts-expect-error
        dispatch(fetchSchemaAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loggedInUser = props.user;

    // The !loggedInUser check is because when we reload the page, we requery the user
    // so loggedInUser will be undefined for a moment
    const hasFullAccessToCampaigns = Boolean(
        !loggedInUser || (loggedInUser && loggedInUser.permissions.canAccessFullCampaigns)
    );

    return (
        <PageWrapper
            header={getPageContainerHeader(window.location.pathname)}
            sidebar={getEmailMarketingSidebar(window.location.pathname, loggedInUser)}
            borderTopLeftColor={
                shouldHideMarketingSidebar(window.location.pathname) ? 'primary' : 'secondary'
            }
        >
            <EmailMarketingRoutes
                hasFullAccessToCampaigns={hasFullAccessToCampaigns}
                canAccessSetup={Boolean(
                    !loggedInUser || (loggedInUser && loggedInUser.permissions.canAccessSetup)
                )}
            />
            <SmsMarketingRoutes
                hasFullAccessToCampaigns={hasFullAccessToCampaigns}
                canAccessSetup={Boolean(
                    !loggedInUser || (loggedInUser && loggedInUser.permissions.canAccessSetup)
                )}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Switch>
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/dashboard'
                    reportKey={DASHBOARD}
                    component={Dashboard}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedListPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/forms'
                    localStorageKey={FORM_VIEW_SEARCH}
                    defaultQuery='?view=board&sort=-modifiedTime'
                    component={FormsListPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <Redirect
                    exact={true}
                    from='/marketing/forms/:formId'
                    to='/marketing/forms/:formId/main'
                />
                <RestrictedRoute
                    path='/marketing/forms/:formId/preview'
                    exact={true}
                    render={(renderProps) => {
                        return <FormPreview {...renderProps} />;
                    }}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/forms/:formId/:tab'
                    reportKey='formEngagement'
                    initialFilterKey='forms'
                    idParam='formId'
                    component={NutClientConfig.hasMultiPageFormsFF ? FormHomePage : FormPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedListPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/connected-forms'
                    localStorageKey={CONNECTED_FORMS_SEARCH}
                    defaultQuery=''
                    component={ConnectedFormsListPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <Redirect
                    exact={true}
                    from='/marketing/connected-forms/:formId'
                    to='/marketing/connected-forms/:formId/main'
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/connected-forms/:formId/:tab'
                    reportKey='formEngagement'
                    initialFilterKey='forms'
                    idParam='formId'
                    component={ConnectedFormsPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/analytics'
                    reportKey='siteAnalytics'
                    component={AnalyticsPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/visitors/:tab'
                    reportKey='mcfxcompanies'
                    component={VisitorsPageWrapper}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <Redirect
                    exact={true}
                    from='/marketing/competitors'
                    to='/marketing/competitors/charts'
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/competitors/:tab'
                    reportKey='competitors'
                    component={CompetitorsPageWrapper}
                    hasAccess={NutClientConfig.hasCompetitorIq && hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <Redirect exact={true} from='/marketing/visitors' to='/marketing/visitors/list' />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/automations'
                    component={MoneyPagesListPage}
                    hasAccess={
                        NutClientConfig.hasMoneyPagesFF && NutClientConfig.hasMarketingAutomations
                    }
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <Redirect
                    exact={true}
                    from='/marketing/automations/:moneyPageId'
                    to='/marketing/automations/:moneyPageId/automation'
                />
                <RestrictedPageRoute
                    // @ts-expect-error
                    exact={true}
                    path='/marketing/automations/:moneyPageId/:tab'
                    component={MoneyPage}
                    idParam='moneyPageId'
                    hasAccess={
                        NutClientConfig.hasMoneyPagesFF && NutClientConfig.hasMarketingAutomations
                    }
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedListPageRoute
                    path='/marketing/prospector'
                    // @ts-expect-error
                    exact={true}
                    localStorageKey={PROSPECTOR_VIEW_SEARCH}
                    component={ProspectorListPage}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                    defaultQuery='?filter%5BhasEmail%5D=true&filter%5BhasLinkedin%5D=true&filter%5BhasPhoneNumber%5D=true'
                />
                <RestrictedRoute
                    path='/marketing/digital-marketing-services'
                    exact={true}
                    render={() => <MarketingServicesPage />}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedRoute
                    path='/marketing/seo-scan'
                    exact={true}
                    render={() => <SEOScanPage user={loggedInUser} />}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedRoute
                    path='/marketing/marketing-resources'
                    exact={true}
                    render={() => <MarketingResourcesPage />}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
                <RestrictedRoute
                    path='/marketing/crm-advertising'
                    exact={true}
                    render={() => <CrmAdvertisingPage />}
                    hasAccess={hasFullAccessToCampaigns}
                    redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
                />
            </Switch>
        </PageWrapper>
    );
}
